import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Inbox, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { mailboxApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/SimpleToast";
import { EmailList } from "@/components/mailbox/EmailList";
import { MailboxRules } from "@/components/mailbox/MailboxRules";
import { ScrollFade } from "@/components/ScrollFade";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "noch nie");

export default function Mailbox() {
  const { user } = useAuth();
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState("open");
  const [listKey, setListKey] = useState(0);

  const loadStatus = useCallback(() => mailboxApi.status().then((r) => setStatus(r.data)).catch(() => {}), []);
  useEffect(() => { loadStatus(); }, [loadStatus, listKey]);

  const syncNow = async () => {
    await mailboxApi.syncNow();
    toast.success("Abruf gestartet – neue Mails erscheinen in Kürze");
    setTimeout(() => { loadStatus(); setListKey((k) => k + 1); }, 8000);
  };

  const c = status?.counts || {};
  return (
    <div className="space-y-6 animate-fade-in" data-testid="mailbox-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3"><Inbox className="w-8 h-8" />Service-Postfach</h1>
          <p className="text-muted-foreground mt-1">{status?.mailbox || "service@r-network.de"} · Abruf alle 5 Minuten (Posteingang und Gesendet)</p>
        </div>
        {user?.role === "admin" && <Button variant="outline" onClick={syncNow} data-testid="mailbox-sync-btn"><RefreshCw className="w-4 h-4 mr-2" />Jetzt abrufen</Button>}
      </div>

      {status && (
        <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-sm rounded-lg border px-3 py-2 ${status.last_error ? "border-amber-300 bg-amber-50 text-amber-800" : "text-muted-foreground"}`} data-testid="mailbox-status">
          {status.last_error ? <AlertTriangle className="w-4 h-4 shrink-0" /> : <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />}
          {status.last_error ? `${status.last_error} (letzter Versuch ${fmt(status.last_attempt_at)})` : `Zuletzt abgerufen: ${fmt(status.last_sync_at)}`}
          <span className="w-full sm:w-auto sm:ml-auto text-xs">Gespeichert werden nur Absender, Empfänger, Betreff, Datum und ein kurzer Textauszug – keine Anhänge.</span>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <ScrollFade>
          <TabsList>
            <TabsTrigger value="open" data-testid="mailbox-tab-open">Zuordnung offen ({c.open ?? "…"})</TabsTrigger>
            <TabsTrigger value="assigned" data-testid="mailbox-tab-assigned">Zugeordnet ({c.assigned ?? "…"})</TabsTrigger>
            <TabsTrigger value="ignored" data-testid="mailbox-tab-ignored">Ignoriert ({c.ignored ?? "…"})</TabsTrigger>
            <TabsTrigger value="rules" data-testid="mailbox-tab-rules">Regeln</TabsTrigger>
          </TabsList>
        </ScrollFade>
        <TabsContent value="rules">{tab === "rules" && <MailboxRules />}</TabsContent>
        {["open", "assigned", "ignored"].map((s) => (
          <TabsContent key={s} value={s}>
            {tab === s && (
              <EmailList key={`${s}-${listKey}`} params={{ status: s }} showTarget testId={`mailbox-list-${s}`} onLoaded={loadStatus}
                emptyText={s === "open" ? "Alles zugeordnet – keine offenen E-Mails." : s === "assigned" ? "Noch keine zugeordneten E-Mails." : "Keine ignorierten E-Mails."} />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
